import { useEffect, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";

/**
 * Session-wide uid → display-name cache — the name equivalent of
 * avatars.ts's photo cache, built for the same reason: a message's
 * embedded sender name is a snapshot from whenever that message was sent
 * or fetched, not a live value. After the one-time name-setup feature
 * shipped, a renamed user's OLD messages kept showing their old name
 * forever, since nothing ever re-resolved it. This bridges that gap the
 * same way the avatar cache already does for photos.
 */

const cache = new Map<string, string>();
const inFlight = new Set<string>();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((fn) => fn());
}

export function subscribeNames(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Records a name we already know, without a round trip. Called wherever
 * a real User object passes through (message senders, conversations),
 * so the common cases never need a fetch. */
export function seedNameFromUser(user: { getUid?: () => string; getName?: () => string } | null | undefined): void {
  const uid = user?.getUid?.();
  const name = user?.getName?.();
  if (!uid || !name) return;
  if (cache.get(uid) === name) return; // no change — don't re-render everything
  cache.set(uid, name);
  emit();
}

/** Directly sets a uid's name — used right after the one-time name-setup
 * completes, so it appears everywhere immediately (including this user's
 * own past messages) rather than only after a reload. */
export function setUserName(uid: string, name: string): void {
  if (!uid || !name || cache.get(uid) === name) return;
  cache.set(uid, name);
  emit();
}

/** Looks a uid up, fetching once if we've never seen it. Deduped. */
function ensureName(uid: string): void {
  if (!uid || cache.has(uid) || inFlight.has(uid)) return;
  inFlight.add(uid);
  CometChat.getUser(uid)
    .then((u) => {
      const name = u?.getName?.();
      if (name) cache.set(uid, name);
    })
    .catch(() => {
      /* leave uncached — the fallback name still renders fine */
    })
    .finally(() => {
      inFlight.delete(uid);
      emit();
    });
}

/** The live name for a uid, falling back to whatever name the caller
 * already has in hand (e.g. one embedded in an older message) until the
 * live one loads or if the lookup fails. */
export function useUserName(uid: string | undefined, fallback: string): string {
  const [name, setName] = useState<string>(() => (uid && cache.get(uid)) || fallback);

  useEffect(() => {
    if (!uid) {
      setName(fallback);
      return;
    }
    setName(cache.get(uid) || fallback);
    ensureName(uid);
    return subscribeNames(() => setName(cache.get(uid) || fallback));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, fallback]);

  return name;
}
